/**
 * HealthDot — silent deployment-parity indicator.
 *
 *   • Calls GET /api/health once on mount, then every 5 minutes.
 *   • Green  = environment matches expectation, build_id present, status=ok.
 *   • Amber  = environment mismatch (e.g. preview build serving prod URL)
 *              OR backend reported status="degraded".
 *   • Red    = endpoint unreachable / non-200.
 *
 * Non-blocking by design: a failure here NEVER throws, NEVER awaits the
 * dashboard, NEVER prevents login. Plain `fetch` (no axios interceptors)
 * with a 4s AbortController to make sure a slow ingress can't stall the
 * UI thread.
 *
 * Admins get a hover/click panel showing environment, build_id, status,
 * timestamp. Non-admins see only the dot — no technical details.
 */
import { useEffect, useRef, useState } from "react";

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes — diagnostic, not real-time
const FETCH_TIMEOUT_MS = 4000;

const COLORS = {
  green:   "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]",
  amber:   "bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.6)]",
  red:     "bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.6)]",
  unknown: "bg-slate-500",
};

const LABELS = {
  green:   "Healthy",
  amber:   "Degraded or environment mismatch",
  red:     "Unreachable",
  unknown: "Checking…",
};

/**
 * Decide which colour the dot should show based on the /api/health
 * response and the environment we expect this deployment to be.
 *
 * Expected environment is inferred from the hostname:
 *   *.preview.emergentagent.com → "preview"
 *   *.com (no preview)           → "production"
 *
 * If the backend reports a different environment than the hostname
 * suggests, that's the most common deploy-parity bug — we surface it
 * as amber.
 */
function classify(data) {
  if (!data) return "red";
  if (data.status && data.status !== "ok") return "amber";
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const expected = host.includes(".preview.") ? "preview" : "production";
  if (data.environment && data.environment !== expected) return "amber";
  return "green";
}

export default function HealthDot({ isAdmin = false }) {
  const [colour, setColour] = useState("unknown");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let cancelled = false;
    let timer;

    const tick = async () => {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
      try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const res = await fetch(`${base}/api/health`, {
          signal: ctrl.signal,
          // The endpoint is public and small — no auth needed.
          credentials: "omit",
        });
        clearTimeout(t);
        if (!res.ok) throw new Error(`status ${res.status}`);
        const body = await res.json();
        if (cancelled) return;
        setData(body);
        setColour(classify(body));
      } catch (_e) {
        clearTimeout(t);
        if (cancelled) return;
        setColour("red");
        setData(null);
      }
    };

    tick();
    timer = setInterval(tick, POLL_INTERVAL_MS);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, []);

  // Close panel on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [open]);

  // Non-admin: render a static, non-interactive dot. No tooltip text
  // beyond an aria-label — never expose env / build_id to end users.
  if (!isAdmin) {
    return (
      <span
        className={`inline-block w-2 h-2 rounded-full ${COLORS[colour]}`}
        data-testid="health-dot"
        data-state={colour}
        aria-label={`System status: ${LABELS[colour]}`}
        title=""
      />
    );
  }

  // Admin: clickable, opens a tiny diagnostic panel.
  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center justify-center w-3 h-3 rounded-full ${COLORS[colour]} hover:scale-125 transition-transform`}
        data-testid="health-dot"
        data-state={colour}
        aria-label={`System status: ${LABELS[colour]} (click for details)`}
        title={LABELS[colour]}
      />
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-64 rounded-lg border border-slate-800/60 bg-[#0B1120] shadow-2xl z-50 p-3 text-[11px] text-slate-300"
          data-testid="health-dot-panel"
        >
          <div className="flex items-center gap-2 mb-2">
            <span
              className={`inline-block w-2 h-2 rounded-full ${COLORS[colour]}`}
            />
            <span className="font-semibold text-slate-100">{LABELS[colour]}</span>
          </div>
          <dl className="space-y-1 font-mono text-[10px] leading-snug">
            <Row k="environment" v={data?.environment} />
            <Row k="build_id" v={data?.build_id} />
            <Row k="status" v={data?.status} />
            <Row k="checked" v={data?.now ? new Date(data.now).toLocaleString("en-GB") : "—"} />
          </dl>
          {colour === "amber" && data?.environment && (
            <p className="mt-2 text-[10px] text-amber-300/80">
              Backend reports{" "}
              <span className="font-mono">{data.environment}</span>; this URL
              looks like{" "}
              <span className="font-mono">
                {window.location.hostname.includes(".preview.")
                  ? "preview"
                  : "production"}
              </span>.
            </p>
          )}
          {colour === "red" && (
            <p className="mt-2 text-[10px] text-red-300/80">
              <code>/api/health</code> did not respond.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-slate-200 truncate" data-testid={`health-${k}`}>
        {v ?? "—"}
      </dd>
    </div>
  );
}
